import React, { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { money } from '@/lib/helpers';
import { Layers, Truck, ChevronRight, Package } from 'lucide-react';

export default function StockReport() {
  const { stores } = useApp();
  const [scope, setScope] = useState('');
  const [tab, setTab] = useState('category');
  const [drill, setDrill] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['stock-summary', scope],
    queryFn: async () => (await api.get('/reports/stock-summary', { params: { store_id: scope } })).data.data,
  });

  const rows = tab === 'category' ? (data?.by_category || []) : (data?.by_party || []);

  return (
    <Page>
      <TopBar title="Stock Report" />
      <div className="px-4 pt-4 space-y-4">
        <div className="rounded-2xl border border-border bg-primary p-4 text-primary-foreground">
          <p className="text-xs opacity-80">Total available stock value</p>
          <p className="font-mono text-3xl font-bold tabular-nums">{money(data?.total_value)}</p>
          <p className="mt-1 text-xs opacity-80">{data?.total_count || 0} garments</p>
        </div>

        <div className="flex items-center gap-2">
          <Select value={scope || 'all'} onValueChange={(v) => setScope(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-10 w-full rounded-xl text-sm" data-testid="stockreport-store"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stores</SelectItem>
              {stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="category" data-testid="stockreport-tab-category"><Layers className="mr-1 h-4 w-4" />By Category</TabsTrigger>
            <TabsTrigger value="party" data-testid="stockreport-tab-party"><Truck className="mr-1 h-4 w-4" />By Party</TabsTrigger>
          </TabsList>
          <TabsContent value={tab} className="mt-4">
            {isLoading ? <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p> :
              rows.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">No stock available.</p> :
              <div className="space-y-2" data-testid="stockreport-list">
                {rows.map((r) => (
                  <button key={r.key} data-testid={`stockreport-row-${r.key}`}
                    onClick={() => setDrill({ type: tab, ...r })}
                    className="flex w-full items-center justify-between rounded-xl border border-border bg-card p-3 text-left active:scale-[0.99] transition-transform">
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary">
                        {tab === 'category' ? <Layers className="h-4 w-4" /> : <Truck className="h-4 w-4" />}
                      </span>
                      <div><p className="text-sm font-semibold">{r.name}</p><p className="text-xs text-muted-foreground">{r.count} items</p></div>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="text-right"><p className="font-mono text-sm font-semibold">{money(r.value)}</p></div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </button>
                ))}
              </div>}
          </TabsContent>
        </Tabs>
      </div>

      <DrillDrawer drill={drill} scope={scope} onClose={() => setDrill(null)} />
    </Page>
  );
}

function DrillDrawer({ drill, scope, onClose }) {
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['stock-detail', drill?.type, drill?.key, scope],
    enabled: !!drill,
    queryFn: async () => (await api.get('/reports/stock-detail', {
      params: drill.type === 'category' ? { category: drill.key, store_id: scope } : { party: drill.key, store_id: scope },
    })).data.data,
  });
  return (
    <Drawer open={!!drill} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DrawerContent className="bg-card">
        <div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" />
        <DrawerHeader><DrawerTitle>{drill?.name} · {money(drill?.value)}</DrawerTitle></DrawerHeader>
        <div className="max-h-[65vh] overflow-y-auto px-4 pb-6">
          {isLoading ? <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p> :
            items.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No items.</p> :
            <div className="space-y-2">
              {items.map((g) => (
                <div key={g.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-3" data-testid={`stockdetail-${g.barcode}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{g.product || '(no name)'}</p>
                    <p className="font-mono text-xs text-muted-foreground">{g.barcode} · {g.category || 'Uncategorised'} · {g.store_name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Package className="h-3 w-3" />{g.supplier_name}</p>
                  </div>
                  <span className="font-mono text-sm font-semibold">{money(g.price)}</span>
                </div>
              ))}
            </div>}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
